// TriggerWatch Device Groups management page — MSP portal
// View, create, edit membership, and delete named device groups used for group deployments

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";
import useSuspended from "../hooks/useSuspended";

const API_URL = config.API_URL;

export default function TriggerWatchDeviceGroups() {
  const navigate = useNavigate();
  const isSuspended = useSuspended();
  const token = sessionStorage.getItem("token");
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingGroupId, setEditingGroupId] = useState(null); // null = not editing
  const [editName, setEditName] = useState("");
  const [editDeviceIds, setEditDeviceIds] = useState([]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchGroups();
    fetchDevices();
  }, []);

  async function fetchGroups() {
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/tw-device-groups`, authHeaders);
      setGroups(res.data);
    } catch (err) {
      console.error("Failed to load device groups", err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDevices() {
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/devices`, authHeaders);
      setDevices(res.data);
    } catch (err) {
      console.error("Failed to load devices", err);
    }
  }

  async function startEdit(group) {
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/tw-device-groups/${group.id}`, authHeaders);
      setEditingGroupId(group.id);
      setEditName(res.data.name);
      setEditDeviceIds(res.data.devices.map(d => d.device_id));
    } catch (err) {
      alert("Failed to load group details.");
    }
  }

  function toggleEditDevice(deviceId) {
    setEditDeviceIds(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
    );
  }

  async function saveEdit() {
    if (!editName.trim()) { alert("Group name is required."); return; }
    if (editDeviceIds.length === 0) { alert("Select at least one device."); return; }
    try {
      await axios.put(
        `${API_URL}/organizations/${ORG_ID}/tw-device-groups/${editingGroupId}`,
        { name: editName, device_ids: editDeviceIds },
        authHeaders
      );
      setEditingGroupId(null);
      fetchGroups();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update group.");
    }
  }

  async function handleDelete(groupId, groupName) {
    if (!window.confirm(`Delete group "${groupName}"? This does not affect past deployments made to it.`)) return;
    try {
      await axios.delete(`${API_URL}/organizations/${ORG_ID}/tw-device-groups/${groupId}`, authHeaders);
      fetchGroups();
    } catch (err) {
      alert("Failed to delete group.");
    }
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar orgName={storedUser.organization_name} />

      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-white mb-1">Device Groups</h1>
        <p className="text-gray-400 text-sm mb-2">
          Named groups of devices for repeatable TriggerWatch deployments.
        </p>
        <p className="text-gray-600 text-xs mb-8">
          To create a new group, select devices on the Dashboard and choose "Deploy to Selected."
        </p>

        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : groups.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No device groups yet.
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map(group => (
              <div key={group.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                {editingGroupId === group.id ? (
                  <div>
                    <div className="mb-3">
                      <label className="block text-sm text-gray-400 mb-1">Group Name</label>
                      <input
                        type="text"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block text-sm text-gray-400 mb-2">Devices ({editDeviceIds.length} selected)</label>
                      <div className="max-h-48 overflow-y-auto bg-gray-800 rounded-lg p-2 space-y-1">
                        {devices.map(d => (
                          <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-700 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              checked={editDeviceIds.includes(d.id)}
                              onChange={() => toggleEditDevice(d.id)}
                            />
                            <span className="text-white">{d.hostname}</span>
                            <span className="text-gray-500 text-xs">{d.manufacturer} {d.model}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={saveEdit}
                        disabled={isSuspended}
                        className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
                          isSuspended ? "bg-gray-800 text-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-500 text-white"
                        }`}
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => setEditingGroupId(null)}
                        className="bg-transparent border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white text-sm px-4 py-2 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-white font-medium text-sm mb-1">{group.name}</h3>
                      <p className="text-gray-400 text-sm mb-1">{group.device_count} device{group.device_count !== 1 ? "s" : ""}</p>
                      <p className="text-gray-600 text-xs">
                        Created by {group.created_by} · {new Date(group.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/triggerwatch/deployments?target_group_id=${group.id}`)}
                        disabled={isSuspended}
                        className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                          isSuspended ? "bg-gray-800 text-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-500 text-white"
                        }`}
                      >
                        Deploy
                      </button>
                      <button
                        onClick={() => startEdit(group)}
                        disabled={isSuspended}
                        className={`text-sm transition-colors ${isSuspended ? "text-gray-600 cursor-not-allowed" : "text-gray-400 hover:text-white"}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(group.id, group.name)}
                        disabled={isSuspended}
                        className={`text-sm transition-colors ${isSuspended ? "text-gray-600 cursor-not-allowed" : "text-red-400 hover:text-red-300"}`}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}